import {
  isChunkLoadError,
  reloadPageOnceForStaleChunk,
  type StaleChunkReloadDeps,
} from "@/lib/chunk-load-recovery";
import { isClerkOptionalPath } from "@/lib/public-routes";
import { isSafariNetworkLoadError } from "@/lib/safari-network-load-error";

export type WindowLoadRecoveryResult =
  | "reloaded"
  | "show_retry"
  | "swallowed"
  | "ignored";

const CLERK_HOST_PATTERN =
  /clerk\.awardparty\.app|@clerk\/clerk-js|ClerkJS:/i;
const CLERK_LOAD_FAILURE_PATTERN =
  /network error|load failed|failed to fetch|loading chunk|failed to load (?:chunk|script)|chunkloaderror|importing a module script failed/i;

function readErrorName(error: object): string {
  return "name" in error && typeof error.name === "string" ? error.name : "";
}

function readErrorMessage(error: object): string {
  return "message" in error && typeof error.message === "string"
    ? error.message
    : "";
}

function readErrorStack(error: object): string {
  return "stack" in error && typeof error.stack === "string" ? error.stack : "";
}

function readErrorText(error: object): string {
  return `${readErrorName(error)}\n${readErrorMessage(error)}\n${readErrorStack(error)}`;
}

export function isClerkFrontendScriptSrc(src: string): boolean {
  if (!src) {
    return false;
  }

  try {
    const url = new URL(src);
    if (url.hostname === "challenges.cloudflare.com") {
      return false;
    }
    if (url.pathname.includes("turnstile")) {
      return false;
    }
    if (url.pathname.startsWith("/v1/")) {
      return false;
    }
    return (
      url.pathname.includes("@clerk/clerk-js") ||
      /clerk\.browser/i.test(url.pathname)
    );
  } catch {
    return (
      /@clerk\/clerk-js|clerk\.browser/i.test(src) &&
      !/challenges\.cloudflare\.com|turnstile/i.test(src)
    );
  }
}

function messageContainsClerkFrontendScript(text: string): boolean {
  const urls = text.match(/https?:\/\/[^\s)"']+/g) ?? [];
  return urls.some((url) => isClerkFrontendScriptSrc(url));
}

function textLooksLikeClerkFrontendLoadFailure(text: string): boolean {
  if (!text.trim()) {
    return false;
  }
  if (/ClerkJS:\s*Network error/i.test(text)) {
    return true;
  }
  if (messageContainsClerkFrontendScript(text)) {
    return true;
  }
  return (
    CLERK_HOST_PATTERN.test(text) && CLERK_LOAD_FAILURE_PATTERN.test(text)
  );
}

export function isClerkFrontendLoadError(error: unknown): boolean {
  const seen = new Set<unknown>();
  let current: unknown = error;

  while (current && !seen.has(current)) {
    seen.add(current);

    if (typeof current === "string") {
      return textLooksLikeClerkFrontendLoadFailure(current);
    }

    if (typeof current === "object") {
      if (textLooksLikeClerkFrontendLoadFailure(readErrorText(current))) {
        return true;
      }
      current = "cause" in current ? current.cause : undefined;
      continue;
    }

    break;
  }

  return false;
}

export function errorFromWindowLoadEvent(event: Event): unknown {
  if (typeof PromiseRejectionEvent !== "undefined") {
    if (event instanceof PromiseRejectionEvent) {
      return event.reason;
    }
  } else if (event.type === "unhandledrejection" && "reason" in event) {
    return event.reason;
  }

  if (typeof ErrorEvent !== "undefined" && event instanceof ErrorEvent) {
    return event.error ?? event.message;
  }

  const target = event.target;
  if (target instanceof HTMLScriptElement && target.src) {
    return new Error(`Failed to load script ${target.src}`);
  }

  return null;
}

export function recoverFromClerkFrontendLoadError(
  error: unknown,
  deps: StaleChunkReloadDeps = {},
): boolean {
  if (!isClerkFrontendLoadError(error)) {
    return false;
  }
  return reloadPageOnceForStaleChunk(deps);
}

function defaultReload() {
  if (typeof window === "undefined") {
    return;
  }
  window.location.reload();
}

export function recoverFromWindowLoadError(
  error: unknown,
  deps: StaleChunkReloadDeps = {},
  context: { pathname?: string | null } = {},
): WindowLoadRecoveryResult {
  const clerkLoadError = isClerkFrontendLoadError(error);
  // The page already rendered without Clerk; a reload or overlay would only hide it.
  if (clerkLoadError && isClerkOptionalPath(context.pathname)) {
    return "ignored";
  }
  if (clerkLoadError || isChunkLoadError(error)) {
    return reloadPageOnceForStaleChunk(deps) ? "reloaded" : "show_retry";
  }
  if (isSafariNetworkLoadError(error)) {
    return "swallowed";
  }
  return "ignored";
}

export function retryAfterClerkOrChunkError(
  error: unknown,
  reset: () => void,
  reload: () => void = defaultReload,
) {
  if (isClerkFrontendLoadError(error) || isChunkLoadError(error)) {
    reload();
    return;
  }
  reset();
}
